/**
 * A record an organization's setup is missing, in the words the offer names it by.
 */
export type MissingSetupRecord = 'location' | 'admin user'

/** One of the reads the offer is decided from. */
export interface SetupRecordRead {
  /** Still in flight, so its count is not yet an answer either way. */
  pending: boolean
  /** Failed, so its count will never be an answer. */
  failed: boolean
  count: number
}

export interface SetupRecordReads {
  locations: SetupRecordRead
  staff: SetupRecordRead
}

/** Whether the read landed, so a count of zero means what it says. */
const isKnown = (read: SetupRecordRead): boolean => !read.pending && !read.failed

/**
 * Which setup records an organization is known to be missing, in creation order —
 * each answering to its own read, so a failed staff read cannot hide an absent
 * location.
 */
export const getMissingSetupRecords = ({
  locations,
  staff,
}: SetupRecordReads): MissingSetupRecord[] => [
  ...(isKnown(locations) && locations.count === 0 ? (['location'] as const) : []),
  ...(isKnown(staff) && staff.count === 0 ? (['admin user'] as const) : []),
]

/**
 * Whether the flow can be entered. The locations read has to have landed, since it
 * decides the opening step and the admin's assignment; the staff read only has to
 * have settled, so a count still in flight is never frozen into the run as an
 * absence, while one that failed still cannot hide the location.
 */
export const canEnterSetup = (reads: SetupRecordReads): boolean =>
  isKnown(reads.locations) &&
  !reads.staff.pending &&
  getMissingSetupRecords(reads).length > 0
